'use client';

import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import styles from './buttonExperience.module.scss';

export type ButtonProps = {
  buttonStyle?: 'primary' | 'secondary';
  preset?: 'regular' | 'small';
  withArrow?: boolean;
  hoverUp?: boolean;
  label?: string;
  href?: string | null;
  target?: boolean;
  disabled?: boolean;
  fontSize?: number | null;
  paddingX?: number | null;
  paddingY?: number | null;
  fullWidth?: boolean;
  customWidth?: number | null;
} & ButtonHTMLAttributes<HTMLButtonElement>;

const PRESETS = {
  regular: {
    paddingX: 48,
    paddingY: 12,
    fontSize: 16,
    lineHeight: '1.48',
  },
  small: {
    paddingX: 32,
    paddingY: 8,
    fontSize: 14,
    lineHeight: '1.45',
  },
} as const;

function getClasses(buttonStyle: ButtonProps['buttonStyle'], hoverUp: boolean) {
  let styleClass = styles.base_primary;
  if (buttonStyle === 'secondary') styleClass = styles.base_secondary;

  const hoverUpClass = hoverUp ? styles.base_hoverUp : '';

  return cn(styleClass, hoverUpClass);
}

function getInlineStyles({
  preset = 'regular',
  fontSize,
  paddingX,
  paddingY,
  fullWidth,
  customWidth,
}: {
  preset?: keyof typeof PRESETS;
  fontSize?: number | null;
  paddingX?: number | null;
  paddingY?: number | null;
  fullWidth?: boolean;
  customWidth?: number | null;
}) {
  const p = PRESETS[preset];

  return {
    paddingLeft: `${paddingX ?? p.paddingX}px`,
    paddingRight: `${paddingX ?? p.paddingX}px`,
    paddingTop: `${paddingY ?? p.paddingY}px`,
    paddingBottom: `${paddingY ?? p.paddingY}px`,
    fontSize: `${fontSize ?? p.fontSize}px`,
    lineHeight: p.lineHeight,

    // width: fullWidth ? '100%' : customWidth ? `${customWidth}px` : 'fit-content',
    width: fullWidth || customWidth ? '100%' : 'fit-content',
    maxWidth: customWidth ? `${customWidth}px` : 'unset',
  } as React.CSSProperties;
}

function getInlineWidth({
  customWidth,
  fullWidth,
}: {
  customWidth?: number | null;
  fullWidth?: boolean;
}) {
  return {
    width: fullWidth ? '100%' : customWidth ? `${customWidth}px` : 'fit-content',
  };
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      buttonStyle = 'primary',
      preset = 'regular',
      withArrow = false,
      hoverUp = true,
      label,
      href,
      target,
      disabled = false,
      fontSize,
      paddingX,
      paddingY,
      fullWidth = false,
      customWidth = null,
      className,
      ...rest
    },
    ref,
  ) => {
    const buttonClasses = getClasses(buttonStyle, hoverUp);

    const inlineStyles = getInlineStyles({
      preset,
      fontSize,
      paddingX,
      paddingY,
      fullWidth,
      customWidth,
    });

    if (href && href !== '' && href !== '#') {
      return (
        <div
          className={cn(disabled ? styles.disabledWrap : '', styles.dw, fullWidth ? 'full' : '')}
          style={getInlineWidth({ customWidth, fullWidth })}
        >
          <Link
            href={href}
            className={cn(styles.base, buttonClasses, disabled ? '_disabled' : '')}
            style={inlineStyles}
            target={target ? '_blank' : '_self'}
          >
            <div className={styles.base_children}>{label}</div>
            {withArrow && <ArrowRight width={20} height={20} />}
          </Link>
        </div>
      );
    }

    return (
      <button
        ref={ref}
        disabled={disabled}
        className={cn(styles.base, buttonClasses, className, fullWidth ? 'full' : '')}
        style={inlineStyles}
        {...rest}
      >
        <div className={styles.base_children}>{label}</div>
        {withArrow && <ArrowRight width={20} height={20} />}
      </button>
    );
  },
);

Button.displayName = 'Button';
